import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { QueryMode, StorePerformanceItem } from '../types'
import { SESSION_STORAGE_KEY } from '../constants'

interface UseQueryConfigProps {
  allUnits: StorePerformanceItem[]
  allQuestionnaires: StorePerformanceItem[]
  selectedCompanyValue: string | number | undefined
  hasAssignedCompany: boolean
}

interface UseQueryConfigReturn {
  queryMode: QueryMode
  setQueryMode: (mode: QueryMode) => void
  handleQueryModeChange: (mode: QueryMode) => void
  selectedUnits: StorePerformanceItem[]
  setSelectedUnits: (items: StorePerformanceItem[]) => void
  selectedQuestionnaires: StorePerformanceItem[]
  setSelectedQuestionnaires: (items: StorePerformanceItem[]) => void
  searchUnit: string
  setSearchUnit: (value: string) => void
  searchQuestionnaire: string
  setSearchQuestionnaire: (value: string) => void
  filteredUnits: StorePerformanceItem[]
  filteredQuestionnaires: StorePerformanceItem[]
  canConsult: boolean
  isUnitSelected: (id: string) => boolean
  isQuestionnaireSelected: (id: string) => boolean
  toggleUnit: (item: StorePerformanceItem) => void
  toggleQuestionnaire: (item: StorePerformanceItem) => void
  areAllUnitsSelected: boolean
  areAllQuestionnairesSelected: boolean
  toggleAllUnits: () => void
  toggleAllQuestionnaires: () => void
  clearSelections: () => void
  unitSelectionType: 'checkbox' | 'radio'
  questionnaireSelectionType: 'checkbox' | 'radio'
  selectedQuestionnairesWithVersions: StorePerformanceItem[]
  hasSelectedQuestionnaireWithVersions: boolean
}

const useQueryConfig = ({
  allUnits,
  allQuestionnaires,
  selectedCompanyValue,
  hasAssignedCompany,
}: UseQueryConfigProps): UseQueryConfigReturn => {
  const savedConfig = (() => {
    if (typeof window === 'undefined') return null
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return null
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  })()

  const [queryMode, setQueryMode] = useState<QueryMode>(savedConfig?.queryMode || 'one-unit-many-questionnaires')
  const [selectedUnits, setSelectedUnits] = useState<StorePerformanceItem[]>(savedConfig?.selectedUnits || [])
  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState<StorePerformanceItem[]>(savedConfig?.selectedQuestionnaires || [])
  const [searchUnit, setSearchUnit] = useState(savedConfig?.searchUnit || '')
  const [searchQuestionnaire, setSearchQuestionnaire] = useState(savedConfig?.searchQuestionnaire || '')
  const previousCompanyValue = useRef<string | number | undefined>(undefined)

  useEffect(() => {
    const currentValue = selectedCompanyValue
    if (previousCompanyValue.current !== currentValue) {
      if (previousCompanyValue.current !== undefined) {
        setSelectedUnits([])
        setSelectedQuestionnaires([])
        setSearchUnit('')
        setSearchQuestionnaire('')
      }
      previousCompanyValue.current = currentValue
    }
  }, [selectedCompanyValue])

  useEffect(() => {
    if (allQuestionnaires.length === 0) return

    setSelectedQuestionnaires((prev) => {
      const allById = new Map(allQuestionnaires.map((q) => [q.id, q]))
      const next = prev
        .map((q) => allById.get(q.id))
        .filter((q): q is StorePerformanceItem => !!q)

      const hasChanged =
        next.length !== prev.length || next.some((q, i) => q !== prev[i])

      return hasChanged ? next : prev
    })
  }, [allQuestionnaires])

  const filteredUnits = useMemo(() => {
    if (!searchUnit.trim()) return allUnits
    const term = searchUnit.toLowerCase()
    return allUnits.filter((u) => u.label.toLowerCase().includes(term))
  }, [allUnits, searchUnit])

  const filteredQuestionnaires = useMemo(() => {
    if (!searchQuestionnaire.trim()) return allQuestionnaires
    const term = searchQuestionnaire.toLowerCase()
    return allQuestionnaires.filter((q) => q.label.toLowerCase().includes(term))
  }, [allQuestionnaires, searchQuestionnaire])

  const restrictsUnitsToOne = queryMode === 'one-unit-many-questionnaires' || queryMode === 'one-unit-one-questionnaire'
  const restrictsQuestionnairesToOne = queryMode === 'many-units-one-questionnaire' || queryMode === 'one-unit-one-questionnaire'

  const unitSelectionType: 'checkbox' | 'radio' = restrictsUnitsToOne ? 'radio' : 'checkbox'
  const questionnaireSelectionType: 'checkbox' | 'radio' = restrictsQuestionnairesToOne ? 'radio' : 'checkbox'

  const selectedQuestionnairesWithVersions = useMemo(
    () => selectedQuestionnaires.filter((q) => Array.isArray(q.versions) && q.versions.length > 0),
    [selectedQuestionnaires]
  )

  const hasSelectedQuestionnaireWithVersions = selectedQuestionnairesWithVersions.length > 0

  const canConsult = selectedUnits.length > 0 && selectedQuestionnaires.length > 0
    && !(queryMode === 'one-unit-many-questionnaires' && selectedQuestionnaires.length < 2)
    && !(queryMode === 'many-units-one-questionnaire' && selectedUnits.length < 2)

  const isUnitSelected = useCallback(
    (id: string) => selectedUnits.some((u) => u.id === id),
    [selectedUnits]
  )

  const isQuestionnaireSelected = useCallback(
    (id: string) => selectedQuestionnaires.some((q) => q.id === id),
    [selectedQuestionnaires]
  )

  const toggleUnit = useCallback((item: StorePerformanceItem) => {
    setSelectedUnits((prev) => {
      const exists = prev.some((u) => u.id === item.id)
      if (restrictsUnitsToOne) {
        if (exists) return []
        return [item]
      }
      if (exists) return prev.filter((u) => u.id !== item.id)
      return [...prev, item]
    })
  }, [restrictsUnitsToOne])

  const toggleQuestionnaire = useCallback((item: StorePerformanceItem) => {
    setSelectedQuestionnaires((prev) => {
      const exists = prev.some((q) => q.id === item.id)
      if (restrictsQuestionnairesToOne) {
        if (exists) return []
        return [item]
      }
      if (exists) return prev.filter((q) => q.id !== item.id)
      return [...prev, item]
    })
  }, [restrictsQuestionnairesToOne])

  const areAllUnitsSelected = useMemo(() => {
    if (filteredUnits.length === 0) return false
    return filteredUnits.every((u) => selectedUnits.some((s) => s.id === u.id))
  }, [filteredUnits, selectedUnits])

  const areAllQuestionnairesSelected = useMemo(() => {
    if (filteredQuestionnaires.length === 0) return false
    return filteredQuestionnaires.every((q) =>
      selectedQuestionnaires.some((s) => s.id === q.id)
    )
  }, [filteredQuestionnaires, selectedQuestionnaires])

  const toggleAllUnits = useCallback(() => {
    if (restrictsUnitsToOne) return
    if (areAllUnitsSelected) {
      setSelectedUnits((prev) =>
        prev.filter((s) => !filteredUnits.some((u) => u.id === s.id))
      )
    } else {
      setSelectedUnits((prev) => {
        const newItems = filteredUnits.filter(
          (u) => !prev.some((s) => s.id === u.id)
        )
        return [...prev, ...newItems]
      })
    }
  }, [areAllUnitsSelected, filteredUnits, restrictsUnitsToOne])

  const toggleAllQuestionnaires = useCallback(() => {
    if (restrictsQuestionnairesToOne) return
    if (areAllQuestionnairesSelected) {
      setSelectedQuestionnaires((prev) =>
        prev.filter((s) => !filteredQuestionnaires.some((q) => q.id === s.id))
      )
    } else {
      setSelectedQuestionnaires((prev) => {
        const newItems = filteredQuestionnaires.filter(
          (q) => !prev.some((s) => s.id === q.id)
        )
        return [...prev, ...newItems]
      })
    }
  }, [areAllQuestionnairesSelected, filteredQuestionnaires, restrictsQuestionnairesToOne])

  const handleQueryModeChange = useCallback((mode: QueryMode) => {
    setQueryMode(mode)
    setSelectedUnits([])
    setSelectedQuestionnaires([])
  }, [])

  const clearSelections = useCallback(() => {
    setSelectedUnits([])
    setSelectedQuestionnaires([])
    setSearchUnit('')
    setSearchQuestionnaire('')
  }, [])

  return {
    queryMode,
    setQueryMode,
    handleQueryModeChange,
    selectedUnits,
    setSelectedUnits,
    selectedQuestionnaires,
    setSelectedQuestionnaires,
    searchUnit,
    setSearchUnit,
    searchQuestionnaire,
    setSearchQuestionnaire,
    filteredUnits,
    filteredQuestionnaires,
    canConsult,
    isUnitSelected,
    isQuestionnaireSelected,
    toggleUnit,
    toggleQuestionnaire,
    areAllUnitsSelected,
    areAllQuestionnairesSelected,
    toggleAllUnits,
    toggleAllQuestionnaires,
    clearSelections,
    unitSelectionType,
    questionnaireSelectionType,
    selectedQuestionnairesWithVersions,
    hasSelectedQuestionnaireWithVersions,
  }
}

export { useQueryConfig }
